import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { playNotificationSound } from "../utils/notificationSound";

const SOCKET_URL =
  process.env.REACT_APP_MEDIA_URL ||
  process.env.REACT_APP_API_BASE_URL?.replace(/\/api.*$/, "")
const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [wsStatus, setWsStatus] = useState("disconnected");
  // The current auth token. Tracked in state (not just read once) so the socket
  // reconnects with the right identity when the user logs in or switches account
  // in the same session — otherwise it keeps whoever was logged in at mount and,
  // e.g., a superadmin would never join the role:1 room (only the prior role:2).
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("token"));
  const [wsOrders, setWsOrders] = useState([]);
  const [events, setEvents] = useState([]);
  // Freshly-placed orders since the admin last opened Order Management —
  // tracked by order_id (not a raw counter) so the Sidebar's badge is exact:
  // a duplicate order:new for the same order_id (reconnect replay, a double
  // emit, etc.) can't inflate it, and if one of these orders gets accepted/
  // cancelled/fails elsewhere before the admin opens Order Management, it's
  // removed from the set instead of staying counted as "waiting" forever.
  // Not derived from wsOrders, which also mixes in order:accepted — this
  // means specifically "new orders waiting," not "any order activity."
  // Cleared via clearNewOrderCount when Order Management mounts.
  const [newOrderIds, setNewOrderIds] = useState(() => new Set());
  const newOrderCount = newOrderIds.size;
  // An order_id already resolved (accepted/changed/cancelled/failed/
  // delivered) is no longer "new and waiting" — drop it from the set if
  // present. A no-op if it was never in there.
  const resolveNewOrder = useCallback((orderId) => {
    if (orderId == null) return;
    setNewOrderIds((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  }, []);
  const [soundMuted, setSoundMuted] = useState(
    () => localStorage.getItem("ws_sound_muted") === "true"
  );
  const mutedRef = useRef(soundMuted);

  const toggleMute = useCallback(() => {
    setSoundMuted((prev) => {
      const next = !prev;
      mutedRef.current = next;
      localStorage.setItem("ws_sound_muted", String(next));
      return next;
    });
  }, []);

  const addOrder = useCallback((order) => {
    setWsOrders((prev) => {
      if (prev.some((o) => o.order_id === order.order_id)) return prev;
      if (!mutedRef.current) playNotificationSound();
      return [order, ...prev].slice(0, 50);
    });
  }, []);

  const addEvent = useCallback((type, data) => {
    if (!mutedRef.current) playNotificationSound();
    setEvents((prev) => [{ type, data, timestamp: Date.now() }, ...prev].slice(0, 100));
  }, []);

  // Re-read the token whenever auth changes (login/logout dispatch `auth:changed`;
  // `storage` covers other tabs) so the connect effect below can react.
  useEffect(() => {
    const sync = () => setAuthToken(localStorage.getItem("token"));
    window.addEventListener("auth:changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("auth:changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    const token = authToken;
    if (!token) {
      setSocket(null);
      setWsStatus("disconnected");
      return;
    }

    const s = io(SOCKET_URL, {
      auth: { token },
      transports: [ "websocket"],
      reconnection: true,
      reconnectionDelay: 5000,
      reconnectionDelayMax: 60000,
      reconnectionAttempts: 10,
    });

    s.on("connect", () => setWsStatus("connected"));
    s.on("disconnect", () => setWsStatus("disconnected"));
    s.on("connect_error", () => setWsStatus("disconnected"));

    // Order events
    // Badge only, deliberately — no addEvent (feeds Header.js's notification
    // bell dropdown) and no addOrder (plays a sound, and wsOrders isn't read
    // anywhere) for every single new order. Just the silent Sidebar count.
    s.on("order:new", (data) => {
      if (data?.order_id != null) {
        setNewOrderIds((prev) =>
          prev.has(data.order_id) ? prev : new Set(prev).add(data.order_id)
        );
      }
    });
    s.on("order:accepted", (data) => {
      addEvent("order:accepted", data);
      addOrder(data);
      resolveNewOrder(data?.order_id);
    });
    s.on("order:status_changed", (data) => {
      addEvent("order:status_changed", data);
      if (data?.order) {
        setWsOrders((prev) =>
          prev.map((o) =>
            o.order_id === data.order.order_id ? { ...o, ...data.order } : o
          )
        );
      }
      resolveNewOrder(data?.order?.order_id);
    });
    s.on("order:delivered", (data) => {
      addEvent("order:delivered", data);
      if (data?.order) {
        setWsOrders((prev) =>
          prev.map((o) =>
            o.order_id === data.order?.order_id ? { ...o, ...data.order } : o
          )
        );
      }
      resolveNewOrder(data?.order?.order_id);
    });
    s.on("order:delivery_assigned", (data) => addEvent("order:delivery_assigned", data));
    // Customer cancelled within the 5-minute window (or any other actor flipped
    // status to Cancelled) — payload is flat (order_id at top level), unlike
    // order:status_changed/order:delivered which nest under data.order.
    s.on("order:cancelled", (data) => {
      addEvent("order:cancelled", data);
      setWsOrders((prev) =>
        prev.map((o) =>
          o.order_id === data?.order_id ? { ...o, order_status: "C", status_label: "Cancelled" } : o
        )
      );
      resolveNewOrder(data?.order_id);
    });
    // Order placed with a customer-entered customization note — sent to
    // SuperAdmin + Admin + the order's Branch Manager only (same audience as
    // vendor:no_stock below).
    s.on("order:customized", (data) => addEvent("order:customized", data));
    s.on("order:failed", (data) => {
      addEvent("order:failed", data);
      resolveNewOrder(data?.order_id ?? data?.order?.order_id);
    });
    s.on("order:escalated", (data) => addEvent("order:escalated", data));
    s.on("order:unassigned", (data) => addEvent("order:unassigned", data));
    s.on("order:no_delivery_boys", (data) => addEvent("order:no_delivery_boys", data));

    // Vendor events
    s.on("vendor:order_accepted", (data) => addEvent("vendor:order_accepted", data));
    s.on("vendor:order_rejected", (data) => addEvent("vendor:order_rejected", data));
    s.on("vendor:no_acceptance", (data) => addEvent("vendor:no_acceptance", data));
    // No vendor has stock for the order → manual assignment needed. Sent to
    // SuperAdmin + Admin + the order's Branch Manager.
    s.on("vendor:no_stock", (data) => addEvent("vendor:no_stock", data));

    // Purchase events
    s.on("purchase:no_person_available", (data) => addEvent("purchase:no_person_available", data));
    s.on("purchase:assigned", (data) => addEvent("purchase:assigned", data));
    s.on("purchase:reassigned", (data) => addEvent("purchase:reassigned", data));
    s.on("purchase:manual_assigned", (data) => addEvent("purchase:manual_assigned", data));
    // Split (>₹1000) events — a second person requested/accepted, or nobody was
    // available to help (purchase and delivery both use the "order:split_*"
    // pair; purchase acceptance additionally fires "purchase:split_accepted").
    s.on("purchase:split_offered", (data) => addEvent("purchase:split_offered", data));
    s.on("purchase:split_accepted", (data) => addEvent("purchase:split_accepted", data));
    s.on("order:split_offered", (data) => addEvent("order:split_offered", data));
    s.on("order:split_accepted", (data) => addEvent("order:split_accepted", data));
    s.on("order:split_exhausted", (data) => addEvent("order:split_exhausted", data));

    // QC events
    s.on("qc:failed", (data) => addEvent("qc:failed", data));
    // Round-robin allocation events: one inspector assigned, or nobody was
    // available/left (falls back to the unassigned pull queue) — surfaced to
    // admin/branch manager for manual follow-up, same as the purchase escalation.
    s.on("qc:assigned", (data) => addEvent("qc:assigned", data));
    s.on("qc:no_inspector_available", (data) => addEvent("qc:no_inspector_available", data));

    // Support events
    s.on("support:new_ticket", (data) => addEvent("support:new_ticket", data));

    // Vendor payment workflow — Superadmin uploaded proof of payment, this
    // specific branch's Branch Admin (+ Super Admin/Admin globally) should
    // go review the screenshot and confirm. Settlement page itself listens
    // on `socket` directly (see useSocket()) to invalidate its own query.
    s.on("vendor_payment:paid", (data) => addEvent("vendor_payment:paid", data));
    // A Branch Admin/Admin just approved a vendor's payout — Superadmin
    // needs to know a new request is waiting on them in the Approved tab.
    s.on("vendor_payment:approved", (data) => addEvent("vendor_payment:approved", data));

    setSocket(s);

    return () => {
      s.removeAllListeners();
      s.disconnect();
    };
  }, [authToken, addOrder, addEvent, resolveNewOrder]);

  const clearWsOrders = useCallback(() => setWsOrders([]), []);
  const clearEvents = useCallback(() => setEvents([]), []);
  const clearNewOrderCount = useCallback(() => setNewOrderIds(new Set()), []);

  // Dev-only: simulate a fake order
  const simulateOrder = useCallback(() => {
    const id = Date.now();
    addOrder({
      order_id: `ORD-DEMO-${id}`,
      customer_name: "Test Customer",
      total_amount: (Math.random() * 900 + 100).toFixed(2),
      order_status: "P",
      payment_status: "pending",
      payment_method: "UPI",
      created_at: new Date().toISOString(),
    });
  }, [addOrder]);

  return (
    <WebSocketContext.Provider value={{
      socket,
      wsStatus,
      wsOrders,
      events,
      newOrderCount,
      clearNewOrderCount,
      soundMuted,
      toggleMute,
      clearWsOrders,
      clearEvents,
      simulateOrder,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  return useContext(WebSocketContext);
}

export function useSocket() {
  const ctx = useContext(WebSocketContext);
  return ctx?.socket;
}
